/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CircularProgress,
  Container,
  Stack,
  Typography,
  Button,
  Alert,
  alpha,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { postApi } from 'views/services/api';

// =============================================================================
// /dashboard/hotelpayments/stripe-success?session_id={CHECKOUT_SESSION_ID}
//
// Página a la que Stripe redirige al hotel tras un Checkout exitoso.
// - Lee session_id del query string
// - Llama a POST api/payments/stripe/confirm
// - Si el backend confirma, libera el paywall (subscriptionStatus = 'active')
//   y redirige al dashboard principal.
// - Es idempotente en el backend (busca Payment existente por gatewaySessionId),
//   así que es seguro recargar esta página.
// =============================================================================
const HotelPaymentsStripeSuccess = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  // 'loading' | 'success' | 'error'
  const [state, setState] = useState('loading');
  const [message, setMessage] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);

  const releasePaywallLocally = () => {
    try {
      const hotelDataStr = localStorage.getItem('hotelData');
      if (hotelDataStr) {
        const hotelData = JSON.parse(hotelDataStr);
        hotelData.subscriptionStatus = 'active';
        localStorage.setItem('hotelData', JSON.stringify(hotelData));
      }
    } catch (e) {
      /* noop */
    }
  };

  const confirmSession = async () => {
    if (!sessionId) {
      setState('error');
      setMessage('No se recibió un identificador de sesión válido de Stripe.');
      return;
    }

    try {
      const res = await postApi('api/payments/stripe/confirm', { session_id: sessionId });

      if (res?.status === 200 || res?.status === 201) {
        setPaymentInfo(res.data?.payments || res.data?.payment || null);
        releasePaywallLocally();
        setState('success');
        setMessage('¡Pago confirmado! Tu plan ha sido activado.');
        toast.success('Pago con Stripe confirmado correctamente');

        // Redirigir al dashboard principal tras un breve delay
        setTimeout(() => {
          window.location.href = '/dashboard/app';
        }, 2000);
      } else {
        setState('error');
        const errMsg =
          res?.response?.data?.error ||
          res?.data?.error ||
          'No se pudo confirmar el pago. Si el cargo se realizó en tu tarjeta, contacta al administrador.';
        setMessage(errMsg);
      }
    } catch (err) {
      console.error('Error confirming Stripe session:', err);
      setState('error');
      setMessage(
        err?.response?.data?.error ||
          'No se pudo confirmar el pago. Si el cargo se realizó en tu tarjeta, contacta al administrador.'
      );
    }
  };

  useEffect(() => {
    confirmSession();
  }, []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const renderLoading = () => (
    <Stack alignItems="center" spacing={3}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.1)
        }}
      >
        <CircularProgress size={48} />
      </Box>
      <Typography variant="h5" fontWeight={700}>
        Confirmando tu pago...
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Estamos validando la sesión con Stripe. Por favor, no cierres esta ventana.
      </Typography>
    </Stack>
  );

  const renderSuccess = () => (
    <Stack alignItems="center" spacing={3}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.success.main, 0.1)
        }}
      >
        <CheckIcon sx={{ fontSize: 56, color: 'success.main' }} />
      </Box>
      <Typography variant="h4" fontWeight={700} color="success.main">
        ¡Pago exitoso!
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        {message}
      </Typography>

      {paymentInfo && (
        <Card
          sx={{
            p: 2,
            width: '100%',
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.05)
          }}
        >
          <Stack spacing={1}>
            {paymentInfo.title && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Plan
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {paymentInfo.title}
                </Typography>
              </Stack>
            )}
            {paymentInfo.amount != null && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Monto
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  ${paymentInfo.amount} USD
                </Typography>
              </Stack>
            )}
            {paymentInfo.confirmationNumber && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  N° Confirmación
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                >
                  {paymentInfo.confirmationNumber}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Card>
      )}

      <Typography variant="caption" color="text.secondary">
        Serás redirigido al panel principal en unos segundos...
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={() => {
          window.location.href = '/dashboard/app';
        }}
      >
        Ir al panel ahora
      </Button>
    </Stack>
  );

  const renderError = () => (
    <Stack alignItems="center" spacing={3}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.error.main, 0.1)
        }}
      >
        <ErrorIcon sx={{ fontSize: 56, color: 'error.main' }} />
      </Box>
      <Typography variant="h5" fontWeight={700} color="error.main">
        No pudimos confirmar tu pago
      </Typography>
      <Alert severity="error" sx={{ width: '100%' }}>
        {message}
      </Alert>
      <Stack direction="row" spacing={1.5}>
        <Button
          variant="outlined"
          startIcon={<LockIcon />}
          onClick={() => navigate('/dashboard/hotelsubscriptions')}
        >
          Volver a Suscripciones
        </Button>
        <Button variant="contained" onClick={confirmSession}>
          Reintentar
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      <Card
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          boxShadow: theme.shadows[4]
        }}
      >
        {state === 'loading' && renderLoading()}
        {state === 'success' && renderSuccess()}
        {state === 'error' && renderError()}
      </Card>
    </Container>
  );
};

export default HotelPaymentsStripeSuccess;
